import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import messages from '../messages';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import ImageCrop from '../../../components/ImageCrop';
import ImageDropZone from '../../../components/ImageDropZone';
import {
  SVG_IMAGE_CODE,
  PNG_IMAGE_CODE,
  GIF_IMAGE_CODE,
  JPG_IMAGE_CODE,
} from '../constants';

import './styles.css';

class ImageDropZoneWithCrop extends React.Component {
  static propTypes = {
    scid: PropTypes.string,
    nevermindAction: PropTypes.func,
    nextButtonText: PropTypes.object,
    nextButtonAction: PropTypes.func,
    savedState: PropTypes.object,
    headerText: PropTypes.object,
    instructionText: PropTypes.object,
    crop: PropTypes.object,
    roundCropper: PropTypes.bool,
    uploaderInstructions1: PropTypes.object,
    uploaderInstructions2: PropTypes.object,
    allowedTypes: PropTypes.array,
  }

  initialState = {
    file: null,
    crop: this.props.crop,
    croppedImageBlob: null, // has the cropped image url
    corruptedUpload: false,
    croppedImageBlobDataStr: null,
  };

  constructor(props) {
    super(props);
    /* istanbul ignore next */
    this.state = props.savedState ? props.savedState : this.initialState;
  }

  componentWillUnmount() {
    this.cleanFiles();
  }

  /* istanbul ignore next */
  onDrop = (files) => {
    files.forEach((file) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        const buffer = reader.result;
        const view = new DataView(buffer);
        const signature = view.getUint32(0, false).toString(16).toUpperCase();
        // Every file has a unique signature, we can collect them and create a data lib.
        const validFormats = new RegExp(`${SVG_IMAGE_CODE}|${PNG_IMAGE_CODE}|${GIF_IMAGE_CODE}|${JPG_IMAGE_CODE}`);

        if (validFormats.test(signature)) {
          const imageReader = new FileReader();
          imageReader.readAsDataURL(file);
          imageReader.onload = () => {
            this.setState({ file: imageReader.result, corruptedUpload: false });
          };
        } else {
          this.setState({ corruptedUpload: true });
        }
      };
    });
  }

  onImageLoaded = (image) => {
    this.imageRef = image;

    if (this.state.croppedImageBlob) {
      this.makeClientCrop(this.state.crop);
      return true;
    }

    const aspect = image.width / image.height;
    const w = image.width * 0.60; // 60% width for cropper as per MATCH-6878
    // If the image is taller than it is wide, the cropper will use the aspect ratio of the carousel tile (8/5)
    // If the image is square or wider than it is tall, the cropper will retain the image's aspect ratio.
    const h = aspect < 1 ? w / (8/5) : w / aspect;

    const initialCrop = {
      ...this.state.crop,
      width: w,
      height: h,
      // 0.5 to place x & y position of cropper in the middle of the image
      x: (image.width - w) * 0.5,
      y: (image.height - h) * 0.5,
      aspect,
    };
    /* istanbul ignore else */
    if (image) {
      this.setState({
        crop: initialCrop,
      });
    }

    this.makeClientCrop(initialCrop);
    return false;
  };

  onCropComplete = (crop) => {
    this.makeClientCrop(crop);
  };

  onCropChange = (crop) => {
    this.setState({ crop });
  };

  getCroppedImgBlob(image, crop, fileName) {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height,
    );

    return new Promise((resolve, reject) => {
      // get image type to use it after crop
      const imageType = image.src.split(';')[0].replace('data:', '');
      canvas.toBlob((blob) => {
        blob.name = fileName;
        window.URL.revokeObjectURL(this.fileUrl);
        this.fileUrl = window.URL.createObjectURL(blob); // keep this may be usefull later
        resolve(blob);
      }, imageType);
    });
  }

  async makeClientCrop(crop) {
    /* istanbul ignore else */
    if (this.imageRef && crop.width && crop.height) {
      const croppedImageBlob = await this.getCroppedImgBlob(
        this.imageRef,
        crop,
        `${this.props.scid}-logo.jpeg`,
      );
      this.setState({ croppedImageBlob });
    }
  }

  cleanFiles = () => {
    // Make sure to revoke the data uris to avoid memory leaks
    URL.revokeObjectURL(this.state.file);
  }

  readBlobAsDataURL = (blob) => {
    const temporaryFileReader = new FileReader();

    return new Promise((resolve, reject) => {
      /* istanbul ignore next */
      temporaryFileReader.onerror = (err) => {
        temporaryFileReader.abort();
        reject(new Error('Unexpected error reading cropped file', err));
      };

      temporaryFileReader.onload = () => {
        resolve(temporaryFileReader.result);
      };
      temporaryFileReader.readAsDataURL(blob);
    });
  };

  goBack = () => {
    this.setState(this.initialState);
  }

  cropNextAction = async () => {
    const { croppedImageBlob } = this.state;
    const dataStr = await this.readBlobAsDataURL(croppedImageBlob);
    this.setState({ croppedImageBlobDataStr: dataStr });
    this.props.nextButtonAction(this.state);
  }

  render() {
    const { crop, file } = this.state;
    const {
      nevermindAction,
      nextButtonText,
      headerText,
      instructionText,
      roundCropper,
      uploaderInstructions1,
      uploaderInstructions2,
      allowedTypes,
    } = this.props;

    /* istanbul ignore next */
    return (file ?
      (
        <React.Fragment>
          <Modal.Header styleName="modal-header-title">
            <FormattedMessage {...headerText} />
          </Modal.Header>
          <Modal.Content styleName="modal-content">
            <ImageCrop
              src={file}
              crop={crop}
              crossorigin="true"
              onImageLoaded={this.onImageLoaded}
              onComplete={this.onCropComplete}
              onChange={this.onCropChange}
              styleName={!roundCropper ? 'photos-instruction-text' : 'round-cropper'}
            />
            {instructionText && <FormattedMessage {...instructionText} />}
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.goBack}>
              <FormattedMessage {...messages.back} />
            </Button>
            <Button
              id="image-crop-next-button"
              primary
              onClick={this.cropNextAction}
            >
              <FormattedMessage {...nextButtonText} />
            </Button>
          </Modal.Actions>
        </React.Fragment>
      ) :
      (
        <Modal.Content>
          <ImageDropZone
            onDrop={this.onDrop}
            multiple={false}
            instructions1={uploaderInstructions1 || messages.editLogoModalInstructions1}
            instructions2={uploaderInstructions2 || messages.editLogoModalInstructions2}
            corrupted={this.state.corruptedUpload}
            allowedTypes={allowedTypes}
          />
          <div styleName="never-mind">
            { /* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Button id="image-drop-nevermind-button" styleName="never-mind-button" onClick={nevermindAction}>
              <FormattedMessage {...messages.nevermindBack} />
            </Button>
          </div>
        </Modal.Content>
      )
    );
  }
}

export default ImageDropZoneWithCrop;
